import axios from "axios";
import fileDownload from "js-file-download";
import moment from "moment";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { Add, Assignment, FileCopy, RotateLeft, ScheduleSend } from "@mui/icons-material";
import { Box, Divider, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Sidebar from "../../Components/Sidebar/Sidebar";
import SidebarButton from "../../Components/Sidebar/SidebarButton";
import SidebarForm from "../../Components/Sidebar/SidebarForm";
import SidebarGroup from "../../Components/Sidebar/SidebarGroup";
import Sort, { SortItem } from "../../Components/Sort";
import NotificationSnackbar from "../../Components/Layout/NotificationSnackbar";
import { RemarketingStatus, UserRole, VehiclePerspective } from "../../system/Domain";
import parseContentDispositionFilename from "../../system/parseContentDispositionFilename";
import useCompoundPlaces from "../../system/useCompoundPlaces";
import useCompoundProcessSteps from "../../system/useCompoundProcessSteps";
import { VehicleQuery } from "../../system/useVehicleQuery";
import useReportRequestCooldown from "../../system/useReportRequestCooldown";
import useUser from "../../system/useUser";
import { VehiclesSearchType } from "./Vehicles";

export interface VehiclesSidebarProps {
	query: VehicleQuery;
	setQuery: Dispatch<SetStateAction<VehicleQuery>>;
	place: string | undefined;
	setPlace: Dispatch<SetStateAction<string | undefined>>;
	resetQuery: () => VehicleQuery;
	searchType: VehiclesSearchType;
	setSearchType: Dispatch<SetStateAction<VehiclesSearchType>>;
}

const sortItems: SortItem[] = [
	{
		title: "Status zugewiesen",
		field: "statusAssigned",
	},
	{
		title: "Platz zugewiesen",
		field: "placeAssigned",
	},
	{
		title: "Eingang",
		field: "stockIn",
	},
];

const VehiclesSidebar = (props: VehiclesSidebarProps) => {
	const history = useHistory();
	const [processSteps] = useCompoundProcessSteps();
	const [compoundPlaces] = useCompoundPlaces();

	const [locations, setLocations] = useState<string[]>([]);
	const [groups, setGroups] = useState<{ [k: string]: string[] }>({});

	const [, , hasRole] = useUser();
	const [requestReport, requestingExport] = useReportRequestCooldown();
	const [message, setMessage] = useState<string>();

	useEffect(() => {
		if (!compoundPlaces) return;

		const locations = compoundPlaces
			.map((item) => item.compound.name)
			.filter((value, index, self) => self.indexOf(value) === index);
		const groups: { [k: string]: string[] } = {};

		locations.forEach((location) => {
			groups[location] = compoundPlaces
				?.filter((f) => f.compound.name === location)
				.map((lotSpace) => lotSpace.groups || [])
				.flat()
				.filter((value, index, self) => self.indexOf(value) === index);
		});

		setLocations(locations);
		setGroups(groups);
	}, [compoundPlaces]);

	const requestVehiclesExport = () => requestReport(async () => {
		await axios.post(`/api/user-reports/vehicles-export`, {
			...props.query,
		});

		setMessage("Der Report wird erstellt. Sie werden per Mail benachrichtigt, sobald er unter \"Reports\" zum Download bereitsteht.");
	});

	const triggerMigrationExport = async () => {
		const response = await axios.get(`/api/vehicles/lot-place-migration/export`, {
			responseType: "blob",
		});

		const filename = parseContentDispositionFilename(response.headers["content-disposition"]);
		fileDownload(response.data, filename);
	};

	return (
		<Sidebar>
			{hasRole(UserRole.AldUser) && (
				<SidebarGroup>
					<SidebarButton
						startIcon={<Add />}
						roles={[UserRole.AldUser]}
						label="Neues Fahrzeug"
						color="primary"
						onClick={() => history.push(`/vehicles/new`)}
					/>
				</SidebarGroup>
			)}
			{props.query.perspective === "Stock" && hasRole(UserRole.LotManager) && (
				<SidebarGroup>
					<SidebarButton
						startIcon={<FileCopy />}
						roles={[UserRole.LotManager]}
						label="Sammelsitzung starten"
						color="primary"
						onClick={async () => {
							const { data } = await axios.post(`/api/sales/collection-sessions`, {});
							history.push(`/vehicles/collection-sessions/${data.id}`);
						}}
					/>
					<SidebarButton
						startIcon={<Assignment />}
						roles={[UserRole.LotManager]}
						label="Sammelsitzungen"
						color="inherit"
						onClick={() => history.push(`/vehicles/collection-sessions`)}
					/>
				</SidebarGroup>
			)}
			<SidebarGroup>
				<SidebarForm>
					<FormControl variant="standard" fullWidth>
						<InputLabel>Perspektive</InputLabel>
						<Select
							value={props.query.perspective}
							onChange={(e) => {
								const newPerspective = e.target.value as VehiclePerspective;

								props.setQuery((q) => ({
									...q,
									perspective: newPerspective,
									currentExitDateFrom: newPerspective === "Out" ? q.currentExitDateFrom : undefined,
									currentEntryDateFrom: newPerspective !== "Out" ? q.currentEntryDateFrom : undefined
								}));
							}}
						>
							<MenuItem value="Stock">Bestand</MenuItem>
							<MenuItem value="In">Eingang</MenuItem>
							<MenuItem value="Out">Ausgang</MenuItem>
							<MenuItem value="Bookmarked">Merkliste</MenuItem>
							<MenuItem value="All">Alle</MenuItem>
						</Select>
					</FormControl>
					<Sort
						items={sortItems}
						value={props.query.sort}
						onChange={(sort) =>
							props.setQuery((q) => ({
								...q,
								sort,
							}))
						}
					/>
					<FormControl variant="standard" fullWidth>
						<InputLabel>Status</InputLabel>
						<Select
							value={props.query.status || "-"}
							onChange={(e) => {
								let value = e.target.value as string | undefined;
								if (value === "-") {
									value = undefined;
								}

								props.setQuery((q) => ({
									...q,
									status: value as RemarketingStatus,
								}));
							}}
						>
							<MenuItem value="-">Alle</MenuItem>
							{processSteps?.map((s) => (
								<MenuItem key={s.status} value={s.status}>
									{s.displayName}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl variant="standard" fullWidth>
						<InputLabel>Suchfeld</InputLabel>
						<Select
							value={props.searchType}
							onChange={(e) => props.setSearchType(e.target.value as VehiclesSearchType)}
						>
							<MenuItem value="fulltext">Volltext</MenuItem>
							<MenuItem value="plate">Kennzeichen</MenuItem>
							<MenuItem value="vin">FIN</MenuItem>
						</Select>
					</FormControl>
					<FormControl variant="standard" fullWidth>
						<InputLabel>Standort</InputLabel>
						<Select
							value={props.query.locationName || "-"}
							onChange={(e) => {
								const value = e.target.value as string;
								props.setQuery((q) => ({
									...q,
									locationName: value === "-" ? undefined : value,
									placeGroup: undefined,
									placeId: undefined,
								}));
							}}
						>
							<MenuItem value="-">Alle</MenuItem>
							{locations && (
								<Box my={2}>
									<Divider />
								</Box>
							)}
							{locations?.map((g) => (
								<MenuItem key={`locationName:${g}`} value={g}>
									{g}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl variant="standard" fullWidth>
						<InputLabel>Platz</InputLabel>
						<Select
							value={props.place || "-"}
							onChange={(e) => {
								const value = e.target.value as string;
								props.setPlace(value === "-" ? undefined : value);
							}}
							disabled={!props.query.locationName}
						>
							<MenuItem value="-">Alle</MenuItem>
							{props.query.locationName && groups[props.query.locationName]?.length > 0 && (
								<Box my={2}>
									<Divider />
								</Box>
							)}
							{props.query.locationName &&
								groups[props.query.locationName]?.length > 0 &&
								groups[props.query.locationName].map((g) => (
									<MenuItem key={g} value={`group:${g}`}>
										{g}
									</MenuItem>
								))}
							{compoundPlaces?.some((f) => f.compound.name === props.query.locationName) && (
								<Box my={2}>
									<Divider />
								</Box>
							)}
							{compoundPlaces
								?.filter((f) => f.compound.name === props.query.locationName)
								.map((p) => (
									<MenuItem key={`${p.id}`} value={p.id}>
										{p.name}
									</MenuItem>
								))}
						</Select>
					</FormControl>

					{props.query.perspective !== "All" && (
						<FormControl fullWidth variant="standard">
							<DatePicker
								format="DD.MM.YYYY"
								label="Tag"
								value={props.query.date === undefined ? null : moment(props.query.date)}
								slotProps={{ textField: { variant: "standard" } }}
								onChange={(d: any) =>
									props.setQuery((q) => ({
										...q,
										date: (d || moment()).startOf("d").toISOString(true),
										currentEntryDateFrom: undefined,
										currentExitDateFrom: undefined
									}))
								}
							/>
						</FormControl>
					)}
					{props.query.perspective !== "Out" && (
						<FormControl fullWidth variant="standard">
							<DatePicker
								format="DD.MM.YYYY"
								sx={{ border: 0 }}
								label="Eingang seit dem"
								slotProps={{ textField: { variant: "standard" } }}
								value={props.query.currentEntryDateFrom === undefined ? null : moment(props.query.currentEntryDateFrom)}
								onChange={(d: any) =>
									props.setQuery((q) => ({
										...q,
										currentEntryDateFrom: (d || moment()).startOf("d").toISOString(true),
										date: undefined
									}))
								}
							/>
						</FormControl>
					)}
					{props.query.perspective === "Out" && (
						<FormControl fullWidth variant="standard">
							<DatePicker
								format="DD.MM.YYYY"
								sx={{ border: 0 }}
								label="Ausgang seit dem"
								slotProps={{ textField: { variant: "standard" } }}
								value={props.query.currentExitDateFrom === undefined ? null : moment(props.query.currentExitDateFrom)}
								onChange={(d: any) =>
									props.setQuery((q) => ({
										...q,
										currentExitDateFrom: (d || moment()).startOf("d").toISOString(true),
										date: undefined
									}))
								}
							/>
						</FormControl>
					)}
				</SidebarForm>
			</SidebarGroup>
			<SidebarGroup>
				<SidebarButton
					startIcon={<ScheduleSend />}
					color="inherit"
					label="Excel-Export"
					onClick={requestVehiclesExport}
					disabled={requestingExport}
				/>
				<SidebarButton
					startIcon={<RotateLeft />}
					label="Filter zurücksetzen"
					color="inherit"
					onClick={props.resetQuery}
				/>
			</SidebarGroup>
			{hasRole(UserRole.AldUser) && (
				<SidebarGroup>
					<SidebarButton
						startIcon={<Assignment />}
						roles={[UserRole.AldUser]}
						label="Migrationsexport"
						color="inherit"
						onClick={triggerMigrationExport}
					/>
					<SidebarButton
						startIcon={<Assignment />}
						roles={[UserRole.AldUser]}
						label="Leaseplan Import"
						color="inherit"
						onClick={() => history.push(`/vehicles/leaseplan-import`)}
					/>
				</SidebarGroup>
			)}
			<NotificationSnackbar message={message} onClose={() => setMessage(undefined)} color="success" />
		</Sidebar>
	);
};

export default VehiclesSidebar;
